import React, { useState } from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { notify } from '../../../../utils/toast';
import { Button } from '../../../../components/ui/Button';
import Modal from '../../../../components/ui/Modal';

interface User {
    id: string;
    name: string;
    accessType?: string;
}

interface TransferModalProps {
    isOpen: boolean;
    users: User[];
    onTransfer: (userId: string) => void | Promise<void>;
    onClose: () => void;
}

type TransferType = 'team' | 'user' | null;

export const TransferModal: React.FC<TransferModalProps> = ({
    isOpen,
    users,
    onTransfer,
    onClose
}) => {
    const [selectedType, setSelectedType] = useState<TransferType>('user');
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [isTransferring, setIsTransferring] = useState(false);

    const handleTransfer = async () => {
        if (selectedType === 'user' && selectedUserId) {
            setIsTransferring(true);
            try {
                await onTransfer(selectedUserId);
                notify.success('Sucesso', {
                    description: 'Atendimento transferido com sucesso'
                });
                // Reset states after transfer
                setSelectedType('user');
                setSelectedUserId(null);
            } finally {
                setIsTransferring(false);
            }
        }
    };

    const handleClose = () => {
        onClose();
        // Reset states on close
        setSelectedType('user');
        setSelectedUserId(null);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
        >
            <Modal.Header onClose={handleClose} className="border-b border-border">
                Transferir conversa
            </Modal.Header>

            <Modal.Body className="p-6 overflow-y-auto">
                <div className="flex gap-3 mb-6">
                    {/* Option: Para equipe */}
                    <div
                        onClick={() => {
                            setSelectedType('team');
                            setSelectedUserId(null);
                        }}
                        className={`flex-1 flex flex-col items-center justify-center p-4 rounded-xl cursor-pointer transition-all duration-200 border ${selectedType === 'team'
                            ? 'bg-primary/5 border-primary shadow-sm'
                            : 'bg-surface border-border hover:bg-muted/5 hover:border-muted'
                            }`}
                    >
                        <span className={`text-xs font-bold uppercase tracking-wider ${selectedType === 'team' ? 'text-primary' : 'text-muted'}`}>
                            PARA UMA EQUIPE
                        </span>
                    </div>

                    {/* Option: Para uma pessoa */}
                    <div
                        onClick={() => setSelectedType('user')}
                        className={`flex-1 flex flex-col items-center justify-center p-4 rounded-xl cursor-pointer transition-all duration-200 border ${selectedType === 'user'
                            ? 'bg-primary/5 border-primary shadow-sm'
                            : 'bg-surface border-border hover:bg-muted/5 hover:border-muted'
                            }`}
                    >
                        <span className={`text-xs font-bold uppercase tracking-wider ${selectedType === 'user' ? 'text-primary' : 'text-muted'}`}>
                            PARA UMA PESSOA
                        </span>
                    </div>
                </div>

                {/* Conditional Content */}
                <div className="mb-2 min-h-[150px]">
                    {selectedType === 'team' && (
                        <div className="flex flex-col items-center justify-center h-32 text-center p-4">
                            <span className="text-body-sm text-muted">A transferência para equipes estará disponível em breve.</span>
                        </div>
                    )}

                    {selectedType === 'user' && (
                        <div className="space-y-1 max-h-56 overflow-y-auto custom-scrollbar pr-1">
                            {users.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-8 text-center">
                                    <div className="w-10 h-10 bg-surface rounded-full flex items-center justify-center mb-2">
                                        <AppIcon name="user-x" className="w-5 h-5 text-muted" />
                                    </div>
                                    <p className="text-body-sm text-muted">
                                        Nenhum usuário disponível
                                    </p>
                                </div>
                            ) : (
                                users.map(user => (
                                    <button
                                        key={user.id}
                                        onClick={() => setSelectedUserId(user.id)}
                                        className={`w-full px-3 py-2.5 flex items-center gap-3 rounded-lg transition-all duration-200 text-left border ${selectedUserId === user.id
                                            ? 'bg-primary/5 border-primary shadow-sm'
                                            : 'bg-transparent border-transparent hover:bg-muted/5'
                                            }`}
                                    >
                                        <div
                                            className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors font-mono ${selectedUserId === user.id
                                                ? 'bg-primary text-primary-foreground'
                                                : 'bg-muted/20 text-muted'
                                                }`}
                                        >
                                            {user.name?.substring(0, 2).toUpperCase() || 'U'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-semibold truncate ${selectedUserId === user.id ? 'text-primary' : 'text-text'
                                                }`}>
                                                {user.name || 'Usuário'}
                                            </p>
                                            {user.accessType && (
                                                <p className={`text-xs font-mono truncate uppercase tracking-tight ${selectedUserId === user.id ? 'text-primary/70' : 'text-muted'}`}>
                                                    {user.accessType}
                                                </p>
                                            )}
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer className="border-t border-border bg-surface flex items-center gap-3">
                <Button
                    variant="outline"
                    onClick={handleClose}
                    className="flex-1"
                    disabled={isTransferring}
                >
                    Cancelar
                </Button>
                <Button
                    variant="default"
                    onClick={handleTransfer}
                    disabled={selectedType !== 'user' || !selectedUserId || isTransferring}
                    className="flex-1 gap-2"
                >
                    {isTransferring ? 'Transferindo...' : 'Transferir'}
                    {!isTransferring && <AppIcon name="arrow-right" className="w-4 h-4" />}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
